/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';

/**
 * Internal dependencies
 */
import StoryPropTypes from '../../../types';
import FormContext from '../../form/context';
import useHandlers from '../../../utils/useHandlers';
import updateProperties from './updateProperties';
import { isEqual } from '../../../../../../utils/isEqual';

const Form = styled.form`
  padding: 0;
  margin: 0;
`;

const AutoSubmitButton = styled.input.attrs({ type: 'submit' })`
  display: none;
`;

function DesignPanel({
  panelType,
  selectedElements,
  selectedElementAnimations,
  onSetProperties,
  attachmentBehavior,
  registerSubmitHandler,
  ...rest
}) {
  const formRef = useRef(null);
  const [shouldResetUpdatesWhenSynced, setShouldResetUpdatesWhenSynced] =
    useState(false);
  const [presubmitHandlers, registerPresubmitHandler] = useHandlers();

  const formContext = useMemo(
    () => ({
      isMultiple: selectedElements.length > 1,
      registerPresubmitHandler,
    }),
    [selectedElements, registerPresubmitHandler]
  );

  const [elementUpdates, setElementUpdates] = useState({});

  const updatedElements = useMemo(() => {
    return selectedElements.map((element) => ({
      ...element,
      ...elementUpdates[element.id],
    }));
  }, [selectedElements, elementUpdates]);

  const internalSubmit = useCallback(
    (updates, onSuccess) => {
      if (Object.keys(updates).length === 0) {
        return;
      }

      const commitUpdates = updates;
      if (presubmitHandlers.length > 0) {
        selectedElements.forEach((element) => {
          const precommitUpdate = updateProperties(
            element,
            updates[element.id],
            /* commitValues */ true
          );
          let commitUpdate = precommitUpdate;
          presubmitHandlers.forEach((handler) => {
            const handlerUpdate = handler(
              { ...element, ...commitUpdate },
              precommitUpdate,
              element
            );
            commitUpdate = { ...commitUpdate, ...handlerUpdate };
          });
          commitUpdates[element.id] = commitUpdate;
        });
      }

      const getCommitUpdate = (element) => commitUpdates[element.id];
      if (attachmentBehavior) {
        onSetProperties(getCommitUpdate, { attachmentBehavior });
      } else {
        onSetProperties(getCommitUpdate);
      }
      onSuccess?.();
    },
    [attachmentBehavior, presubmitHandlers, selectedElements, onSetProperties]
  );

  const onSubmit = registerSubmitHandler(
    useCallback(
      (evt) => {
        const onSuccess = () => {
          if (evt) {
            // Reset.
            setShouldResetUpdatesWhenSynced(true);
          }
        };

        if (evt) {
          evt.preventDefault();
        }

        internalSubmit(elementUpdates, onSuccess);
      },
      [internalSubmit, elementUpdates]
    )
  );

  const submit = useCallback(() => {
    // The `setTimeout()` below only depends on the `ref` and thus save from
    // dismount issues.
    // eslint-disable-next-line @wordpress/react-no-unsafe-timeout
    setTimeout(() => {
      const form = formRef.current;
      if (form) {
        form.dispatchEvent(
          new window.Event('submit', { cancelable: true, bubbles: true })
        );
      }
    });
  }, []);

  const pushUpdate = useCallback(
    (update, submitArg = false, { skipPresubmit = false } = {}) => {
      if (submitArg && skipPresubmit) {
        // An immediate commit replaces staged values for the same properties.
        // Otherwise an earlier Custom/type edit can mask subsequent Undo/Redo
        // forever because its staged recipe will never equal the live story.
        setElementUpdates((previous) => {
          const remaining = { ...previous };
          selectedElements.forEach((element) => {
            if (!remaining[element.id]) return;
            const pending = { ...remaining[element.id] };
            const committed = updateProperties(element, update, true);
            Object.keys(committed).forEach((key) => delete pending[key]);
            if (Object.keys(pending).length) remaining[element.id] = pending;
            else delete remaining[element.id];
          });
          return Object.keys(previous).length ? remaining : previous;
        });
        onSetProperties((currentProperties) =>
          updateProperties(currentProperties, update, /* commitValues */ true)
        );
        return;
      }

      setElementUpdates((prevUpdates) => {
        const newUpdates = {};
        selectedElements.forEach((element) => {
          const prevUpdatedElement = { ...element, ...prevUpdates[element.id] };
          const newUpdate = updateProperties(prevUpdatedElement, update, false);
          newUpdates[element.id] = { ...prevUpdates[element.id], ...newUpdate };
        });
        return newUpdates;
      });
      if (submitArg) {
        submit();
      }
    },
    [onSetProperties, selectedElements, submit]
  );

  const pushUpdateForObject = useCallback(
    (propertyName, update, defaultObject, submitArg = false) => {
      pushUpdate((prevUpdatedElement) => {
        const prevObject = prevUpdatedElement[propertyName] || defaultObject;

        return {
          [propertyName]: update
            ? {
                ...prevObject,
                ...updateProperties(prevObject, update, false),
              }
            : null,
        };
      }, submitArg);
    },
    [pushUpdate]
  );

  const animationsUpdatesSynced = useMemo(() => {
    let animationsSynced = true;

    const elementIds = Object.keys(elementUpdates);

    for (const elementIdIndex in Object.keys(elementUpdates)) {
      const elementId = elementIds[elementIdIndex];
      const allElementUpdates = elementUpdates[elementId];
      let animationUpdate = allElementUpdates?.animation
        ? { ...allElementUpdates.animation }
        : null;

      if (!animationUpdate) {
        continue;
      }

      if ('targets' in animationUpdate) {
        delete animationUpdate.targets;
      }

      const animationsForElement = selectedElementAnimations
        .filter(({ targets }) => targets.includes(elementId))
        .map(({ targets, ...animation }) => animation);

      if (animationsForElement.length === 0) {
        const wasAnimationDeleted = Boolean(animationUpdate.delete);

        if (wasAnimationDeleted) {
          continue;
        }

        animationsSynced = false;
        break;
      }

      const isSynced = isEqual(animationUpdate, animationsForElement[0]);

      if (!isSynced) {
        animationsSynced = false;
        break;
      }
    }

    return animationsSynced;
  }, [elementUpdates, selectedElementAnimations]);

  const nonAnimationsUpdatesSynced = useMemo(() => {
    // animations get added to the page rather than the element
    return isEqual(selectedElements, updatedElements, ['animation']);
  }, [selectedElements, updatedElements]);

  useEffect(() => {
    const allUpdatesSynced =
      animationsUpdatesSynced && nonAnimationsUpdatesSynced;

    if (shouldResetUpdatesWhenSynced && allUpdatesSynced) {
      setElementUpdates({});
      setShouldResetUpdatesWhenSynced(false);
    }
  }, [
    animationsUpdatesSynced,
    nonAnimationsUpdatesSynced,
    shouldResetUpdatesWhenSynced,
    updatedElements,
  ]);

  useEffect(() => {
    return () => {
      submit();
    };
  }, []);

  const Panel = panelType;
  return (
    <Form ref={formRef} onSubmit={onSubmit}>
      <AutoSubmitButton />
      <FormContext.Provider value={formContext}>
        <Panel
          selectedElements={updatedElements}
          selectedElementAnimations={selectedElementAnimations}
          submittedSelectedElements={selectedElements}
          pushUpdate={pushUpdate}
          pushUpdateForObject={pushUpdateForObject}
          submit={submit}
          {...rest}
        />
      </FormContext.Provider>
    </Form>
  );
}

DesignPanel.propTypes = {
  panelType: PropTypes.func.isRequired,
  selectedElements: PropTypes.arrayOf(StoryPropTypes.element).isRequired,
  onSetProperties: PropTypes.func.isRequired,
  registerSubmitHandler: PropTypes.func.isRequired,
  attachmentBehavior: PropTypes.oneOf(['rebase']),
};

export default DesignPanel;
